/**
 * Seeds visit-form `expense` from procedure autoFix / fixSum.
 * Never writes `additionalExpense` — leave the form control alone (manual only).
 *
 * autoFix off            → 0
 * autoFix + fixSum > 0   → fixSum (not product sum)
 * autoFix + no fixSum    → productExpense
 */
export function seedAutoFixExpense(
  autoFix: boolean | null | undefined,
  fixSum: number | string | null | undefined,
  productExpense: number
): number {
  if (!autoFix) {
    return 0;
  }
  const fixed = Number(fixSum);
  if (Number.isFinite(fixed) && fixed > 0) {
    return +fixed.toFixed(2);
  }
  const products = Number(productExpense);
  return Number.isFinite(products) && products > 0 ? +products.toFixed(2) : 0;
}

/**
 * Cost of one recipe line, on the `salePricePerItem` basis (beaut-458).
 * Grams win over quantity when both are present.
 *
 * `grPerItem > 0` is not optional: without it, a line carrying grams but no
 * grams-per-item divides by zero, and the resulting Infinity — or NaN when the
 * field is absent — propagates into the expense total and from there into the
 * specialist's salary deduction.
 */
export function productLinePrice(line: {
  quantitySum?: number | string | null;
  grSum?: number | string | null;
  product?: {
    salePricePerItem?: number | string | null;
    grPerItem?: number | string | null;
  } | null;
}): number {
  const perItem = Number(line?.product?.salePricePerItem) || 0;
  if (perItem <= 0) {
    return 0;
  }

  const grSum = Number(line?.grSum) || 0;
  const grPerItem = Number(line?.product?.grPerItem) || 0;
  if (grSum > 0 && grPerItem > 0) {
    return +((perItem / grPerItem) * grSum).toFixed(2);
  }

  const quantitySum = Number(line?.quantitySum) || 0;
  if (quantitySum > 0) {
    return +(perItem * quantitySum).toFixed(2);
  }

  return 0;
}

/** True when product edits should refresh expense (autoFix on, no fixed sum). */
export function shouldTrackProductExpense(
  autoFix: boolean | null | undefined,
  fixSum: number | string | null | undefined
): boolean {
  if (!autoFix) return false;
  const fixed = Number(fixSum);
  return !(Number.isFinite(fixed) && fixed > 0);
}
